/**
 * matrix-board 디자인 토큰 (단일 소스 of truth)
 *
 * 근거 문서: docs/design.md. 두 파일은 항상 동기화한다 — 여기 값을 바꾸면 design.md도 같이 고친다.
 * 원칙: 축마다 값을 하나(또는 한 스케일)만 정하고 전역에서 이 토큰을 import 해 쓴다.
 * 하드코딩한 hex/px 는 PR 린트 위반으로 본다.
 *
 * 색감 규칙: 흰 배경 + 블루 강조(#2563eb), 다크 테마 금지.
 *
 * 결정 로그 (2026-06-23):
 *  - Flow/Figma/Toss/Adobe/Soomgo 5개 후보를 docs/design-preview.html 로 비교 → 밀도는 현재
 *    (ctrl 32 / radius 8·12·16 / 13px) 유지로 확정. 레퍼런스 비교 종료(preview = 결정 아카이브).
 *  - 강조색: 보라 #7c3aed → 블루 #2563eb 전환(같은 날). 로고(파랑+그레이)와 제품 UI 강조색을
 *    블루로 통일. 코드 내 보라 잔재(#7c3aed/#f3e8ff/#6d28d9 및 파생 램프)는 블루로 치환.
 */

// 1. 강조색 (Accent) — 블루 (2026-06-23: 보라 #7c3aed → 블루 전환, 로고와 통일)
export const accent = '#2563eb';        // 강조·선택·활성 테두리·primary (blue-600)
export const accentSoft = '#dbeafe';    // 강한 활성 배경(선택 도구·우측독 활성·내 반응 칩) (blue-100)
export const accentSurface = '#eff6ff'; // 옅은 선택/hover 면(토글 선택·리스트 활성행·연한 강조 버튼) (blue-50)
export const accentStrong = '#1d4ed8';  // hover/pressed (blue-700)

// 1b. 워크플로 accent — 블루로 통일(2026-06-24). 과거 teal 예외 폐기, 일반 accent와 동일.
export const workflowAccent = '#2563eb';
export const workflowAccentStrong = '#1d4ed8';
export const workflowAccentSoft = '#dbeafe';

// 1c. 브랜드 / 로고 (flow 워드마크) — 파랑 + 그레이 톤.
//     UI 강조색(accent)도 블루로 통일됨 — 로고는 wave에 blue-500(#3b82f6), UI accent는 blue-600.
//     같은 블루 패밀리. logo* 는 로고/스플래시/브랜드 표기 전용.
export const logoInk = '#1e293b';        // 워드마크 글자 (다크 슬레이트 그레이)
export const logoWave = '#3b82f6';       // 물결 (블루)
export const logoWaveStrong = '#2563eb'; // 진한 블루 (호버/강조·작은 사이즈 대비용)
export const logoInkInverse = '#e2e8f0'; // 어두운 배경에서 글자 반전색

// 2. 중립색 (Neutral ramp)
export const bg = '#ffffff';
export const surfaceAlt = '#f9fafb';
export const border = '#e5e7eb';
export const borderStrong = '#d1d5db';
export const text = '#1f2937';
export const textMuted = '#6b7280';
export const textFaint = '#9ca3af';

// 시맨틱(의미 전용, 강조색과 분리 — 장식용 사용 금지)
export const danger = '#b91c1c';
export const dangerSoft = '#fecaca';
export const success = '#16a34a';
export const warning = '#f59e0b';

// 3. 모서리 반경 (Radius)
export const radiusSm = 8;     // 칩·작은 버튼·입력·썸네일
export const radiusMd = 12;    // floating chrome(툴바·독·카드)
export const radiusLg = 16;    // 모달·큰 패널
export const radiusFull = 9999;

// 4. 그림자 (Shadow) — 광원 위-왼쪽 고정, 3단계
export const shadowSm = '0 1px 3px rgba(17,24,39,0.10)';
export const shadowMd = '0 4px 12px rgba(17,24,39,0.12)';
export const shadowLg = '0 12px 32px rgba(17,24,39,0.18)';

// 4b. 포커스 링 — 입력/버튼 focus 시 1종 (현재 2px/4px 혼용 → 통일)
export const focusRing = `0 0 0 3px ${accentSoft}`;

// 5. 간격 (Spacing) — 4 기반 스케일
export const space = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32, xxxl: 48 };

// 6. 컨트롤 높이 (Control height)
export const ctrlBar = 44;    // floating 툴바/독 정사각 버튼
export const ctrlBase = 32;   // 입력·일반 버튼·드롭다운
export const ctrlChip = 24;   // 칩·뱃지·작은 토글
export const metabar = 48;    // 상단 메타바 높이

// 7. 타입 스케일 (Type)
export const fontFamily = 'system-ui, -apple-system, "Pretendard", sans-serif';
export const textXs = 11;   // 칩·캡션·메타
export const textSm = 13;   // 본문·입력·메뉴
export const textMd = 14;   // 패널 제목·강조 라벨 (weight 600)
export const textLg = 16;   // 모달 제목 (weight 700)

// 8. 아이콘 (Icon) — react-icons/lu(Lucide) 단일, size 15~16(툴바 18~20)
export const iconSize = 16;
export const iconSizeBar = 18;

// 9. 모션 (Motion)
export const transition = '150ms ease';       // 빠른 호버/토글
export const transitionPanel = '200ms ease';  // 패널 슬라이드

// 묶음 export (구조분해로 한 번에 쓰기)
const tokens = {
  accent, accentSoft, accentSurface, accentStrong,
  workflowAccent, workflowAccentStrong, workflowAccentSoft,
  logoInk, logoWave, logoWaveStrong, logoInkInverse,
  bg, surfaceAlt, border, borderStrong, text, textMuted, textFaint,
  danger, dangerSoft, success, warning,
  radiusSm, radiusMd, radiusLg, radiusFull,
  shadowSm, shadowMd, shadowLg, focusRing,
  space,
  ctrlBar, ctrlBase, ctrlChip, metabar,
  fontFamily, textXs, textSm, textMd, textLg,
  iconSize, iconSizeBar,
  transition, transitionPanel,
};

export default tokens;
